import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Gamepad2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    if (t) setToken(t);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !password) {
      toast.error('Tokenul și parola sunt obligatorii');
      return;
    }
    if (password !== confirm) {
      toast.error('Parolele nu se potrivesc');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success('Parola a fost resetată');
      setTimeout(() => navigate('/login'), 1200);
    } catch (error) {
      toast.error(error.message || 'Resetare eșuată');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF0099]/10 via-transparent to-[#00FF94]/10" />
        <div className="relative z-10 text-center max-w-lg">
          <h2 className="text-5xl font-bold mb-4">
            <span className="text-[#00FF94]">RESET.</span>
            <br />
            <span className="text-white">REGAIN.</span>
          </h2>
          <p className="text-xl text-[#A1A1AA]">Setează o parolă nouă.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00FF94] to-[#00F0FF] mb-4">
              <Gamepad2 className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-gradient">WORLD GAMING</h1>
            <p className="mt-2 text-muted-foreground">Resetare parolă</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="token" className="text-sm font-medium text-[#A1A1AA]">TOKEN</Label>
              <Input
                id="token"
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Token"
                className="h-12 bg-[#18181B] border-[#27272A] focus:border-[#00FF94] text-white placeholder:text-[#52525B]"
                required
                data-testid="reset-token"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#A1A1AA]">PAROLĂ NOUĂ</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-[#18181B] border-[#27272A] focus:border-[#00FF94] text-white placeholder:text-[#52525B]"
                required
                data-testid="reset-new-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-sm font-medium text-[#A1A1AA]">CONFIRMĂ PAROLA</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-[#18181B] border-[#27272A] focus:border-[#00FF94] text-white placeholder:text-[#52525B]"
                required
                data-testid="reset-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-primary text-base font-semibold"
              data-testid="reset-submit-button"
            >
              {loading ? 'Se trimite...' : 'Resetează parola'}
            </Button>
          </form>

          <p className="text-center text-[#A1A1AA]">
            <Link
              to="/login"
              className="text-[#00FF94] hover:text-[#00F0FF] transition-colors font-medium"
              data-testid="reset-back-login"
            >
              Înapoi la conectare
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
